/**
 * Widget 性能监控服务
 *
 * 跟踪加载时间、交互事件和错误
 */

export interface PerformanceMetric {
  type: string;
  timestamp: number;
  data: any;
}

export interface MonitorConfig {
  debug?: boolean;
  apiBaseUrl?: string;
  enableAnalytics?: boolean;
  analyticsEndpoint?: string;
}

export class WidgetMonitor {
  private config: MonitorConfig;
  private metrics: PerformanceMetric[] = [];
  private startTime: number = 0;

  constructor(config: MonitorConfig) {
    this.config = config;
    this.startTime = Date.now();
  }

  updateConfig(config: MonitorConfig): void {
    this.config = {
      ...this.config,
      ...config,
    };
  }

  /**
   * 跟踪加载时间
   */
  trackLoadTime(): void {
    if (typeof performance === 'undefined') return;

    const timing = performance.timing;
    const loadTime = timing.loadEventEnd - timing.navigationStart;

    this.report('load_time', {
      duration: loadTime,
      navigationStart: timing.navigationStart,
      domContentLoaded: timing.domContentLoadedEventEnd,
      loadComplete: timing.loadEventEnd
    });
  }

  /**
   * 跟踪交互
   */
  trackInteraction(action: string, data?: any): void {
    this.report('interaction', {
      action,
      timestamp: Date.now(),
      ...data
    });
  }

  /**
   * 跟踪错误
   */
  trackError(error: Error): void {
    this.report('error', {
      message: error.message,
      stack: error.stack,
      name: error.name,
      timestamp: Date.now()
    });
  }

  /**
   * 跟踪性能指标
   */
  trackPerformance(name: string, duration: number): void {
    this.report('performance', {
      name,
      duration,
      timestamp: Date.now()
    });
  }

  /**
   * 上报数据
   */
  private report(type: string, data: any): void {
    const metric: PerformanceMetric = {
      type,
      timestamp: Date.now(),
      data
    };

    this.metrics.push(metric);

    // 开发模式：控制台输出
    if (this.config.debug) {
      console.log(`[SmartCS:Monitor:${type}]`, data);
    }

    // 生产环境：发送到监控服务
    if (this.shouldSendAnalytics()) {
      this.sendToAnalytics(metric).catch(() => {
        // 静默失败
      });
    }
  }

  private shouldSendAnalytics(): boolean {
    return Boolean(this.config.enableAnalytics && (this.config.analyticsEndpoint || this.config.apiBaseUrl));
  }

  /**
   * 发送到分析服务
   */
  private async sendToAnalytics(metric: PerformanceMetric): Promise<void> {
    try {
      const endpoint = this.config.analyticsEndpoint || `${this.config.apiBaseUrl}/api/v1/analytics`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(metric)
      });

      // 静默处理 404 和其他错误，不影响用户体验
      if (!response.ok && this.config.debug) {
        console.debug(`[SmartCS:Monitor] Analytics endpoint returned ${response.status}`);
      }
    } catch (error) {
      // 网络错误或其他问题，静默处理
      if (this.config.debug) {
        console.debug('[SmartCS:Monitor] Failed to send analytics:', error);
      }
    }
  }

  /**
   * 获取所有指标
   */
  getMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  /**
   * 清除指标
   */
  clearMetrics(): void {
    this.metrics = [];
  }

  /**
   * 获取性能摘要
   */
  getSummary(): {
    totalTime: number;
    interactionCount: number;
    errorCount: number;
    metrics: PerformanceMetric[];
  } {
    return {
      totalTime: Date.now() - this.startTime,
      interactionCount: this.metrics.filter(m => m.type === 'interaction').length,
      errorCount: this.metrics.filter(m => m.type === 'error').length,
      metrics: this.metrics
    };
  }
}

/**
 * 全局监控实例
 */
let monitorInstance: WidgetMonitor | null = null;

export function getMonitor(config?: MonitorConfig): WidgetMonitor {
  if (!monitorInstance && config) {
    monitorInstance = new WidgetMonitor(config);
  } else if (monitorInstance && config) {
    monitorInstance.updateConfig(config);
  }
  return monitorInstance as WidgetMonitor;
}
